import React from 'react';
import { ArrowLeft, Shield, Eye, Database, Lock, Mail, Cookie } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const lastUpdated = 'December 15, 2025';

  return (
    <div className="min-h-screen bg-[#0a0b0c]">
      {/* Header */}
      <div className="bg-[#131416] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white/70 hover:text-white hover:bg-white/5"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          <img 
            src="https://customer-assets.emergentagent.com/job_tactical-sports/artifacts/00wc7m32_GWINS%20Gear%20Logo.jpg" 
            alt="GWINS" 
            className="h-8 w-auto"
          />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <Shield className="h-16 w-16 text-[#00d4aa] mx-auto mb-4" />
          <h1 className="text-4xl font-black text-white uppercase mb-2">Privacy Policy</h1>
          <p className="text-white/60">Last updated: {lastUpdated}</p>
        </div>

        <div className="space-y-8 text-white/80">
          {/* Introduction */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Eye className="h-5 w-5 text-[#00d4aa]" />
              Introduction
            </h2>
            <p className="leading-relaxed">
              GWINS Gear ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services, including our community platform, e-commerce features, and related services.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Database className="h-5 w-5 text-[#00d4aa]" />
              Information We Collect
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-white font-semibold mb-2">Personal Information</h3>
                <ul className="list-disc list-inside space-y-1 text-white/70">
                  <li>Name and nickname (for player profiles)</li>
                  <li>Email address</li>
                  <li>Profile photos you upload</li>
                  <li>Location information you provide</li>
                  <li>Team affiliations and paintball experience</li>
                </ul>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">Automatically Collected Information</h3>
                <ul className="list-disc list-inside space-y-1 text-white/70">
                  <li>Browser type and version</li>
                  <li>Device information</li>
                  <li>IP address</li>
                  <li>Pages visited and time spent</li>
                  <li>Referring website addresses</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4">How We Use Your Information</h2>
            <ul className="list-disc list-inside space-y-2 text-white/70">
              <li>To create and manage your player or team profile</li>
              <li>To facilitate community features like messaging and social feeds</li>
              <li>To process orders and transactions through our Shopify integration</li>
              <li>To send you updates, newsletters, and promotional materials (with your consent)</li>
              <li>To improve our website and services</li>
              <li>To respond to your inquiries and support requests</li>
              <li>To detect and prevent fraud or unauthorized access</li>
            </ul>
          </section>

          {/* Cookies */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Cookie className="h-5 w-5 text-[#00d4aa]" />
              Cookies and Tracking Technologies
            </h2>
            <p className="mb-4">
              We use cookies and similar tracking technologies to enhance your experience on our website. These include:
            </p>
            <ul className="list-disc list-inside space-y-2 text-white/70">
              <li><strong className="text-white">Essential Cookies:</strong> Required for basic site functionality and security</li>
              <li><strong className="text-white">Analytics Cookies:</strong> Help us understand how visitors interact with our site</li>
              <li><strong className="text-white">Preference Cookies:</strong> Remember your settings and preferences</li>
            </ul>
            <p className="mt-4">
              You can control cookie settings through your browser preferences. Note that disabling certain cookies may affect site functionality.
            </p>
          </section>

          {/* Data Security */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Lock className="h-5 w-5 text-[#00d4aa]" />
              Data Security
            </h2>
            <p>
              We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          {/* Third-Party Services */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4">Third-Party Services</h2>
            <p className="mb-4">We may share your information with trusted third-party service providers:</p>
            <ul className="list-disc list-inside space-y-2 text-white/70">
              <li><strong className="text-white">Shopify:</strong> For e-commerce and payment processing</li>
              <li><strong className="text-white">Google:</strong> For authentication services</li>
              <li><strong className="text-white">Analytics providers:</strong> To help us understand site usage</li>
            </ul>
          </section>

          {/* Your Rights */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4">Your Rights</h2>
            <p className="mb-4">Depending on your location, you may have the following rights:</p>
            <ul className="list-disc list-inside space-y-2 text-white/70">
              <li>Access your personal data</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to processing of your data</li>
              <li>Request data portability</li>
              <li>Withdraw consent at any time</li>
            </ul>
          </section>

          {/* Contact */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Mail className="h-5 w-5 text-[#00d4aa]" />
              Contact Us
            </h2>
            <p>
              If you have questions about this Privacy Policy or wish to exercise your rights, please contact us at:
            </p>
            <p className="mt-4 text-[#00d4aa] font-semibold">info@gwinsgear.com</p>
          </section>

          {/* Changes */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4">Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
